"use client";

import FileText from "lucide-react/dist/esm/icons/file-text";
import Calendar from "lucide-react/dist/esm/icons/calendar";
import Download from "lucide-react/dist/esm/icons/download";
import Shield from "lucide-react/dist/esm/icons/shield";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import type { AssetDetail } from "@/hooks/useAssetDetail";
import { formatFileSize } from "@/lib/format";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { VersionHistory } from "./VersionHistory";
import { MarkdownRenderer } from "@/components/ui/markdown-renderer";
interface AssetInfoProps {
  asset: AssetDetail;
  hasAccess?: boolean;
}

export function AssetInfo({ asset, hasAccess = false }: AssetInfoProps) {
  const getLicenseInfo = (type: string | null) => {
    switch (type) {
      case "uefn_map_use":
        return { label: "UEFN Map Use", description: "Use in monetized Fortnite islands — you keep 100% of island revenue. No redistribution." };
      case "revenue_share":
        return { label: "Revenue Share", description: "Use in Fortnite islands — a percentage of your island revenue is shared with the seller." };
      case "no_sharing":
        return { label: "Personal Use Only", description: "Use in your own projects. No sharing or redistribution of asset files." };
      case "sharing_allowed":
        return { label: "Sharing Allowed", description: "Share with team members. Non-commercial redistribution OK." };
      case "custom":
        return { label: "Custom License", description: "Seller-defined terms — read carefully below." };
      default:
        return { label: "Standard License", description: "Standard marketplace license terms apply." };
    }
  };

  return (
    <Accordion type="multiple" defaultValue={["description", "details"]} className="w-full">
      {/* Description */}
      <AccordionItem value="description">
        <AccordionTrigger className="text-lg font-semibold">
          About this asset
        </AccordionTrigger>
        <AccordionContent>
          {asset.description ? (
            <MarkdownRenderer content={asset.description} />
          ) : (
            <p className="text-muted-foreground">No description provided.</p>
          )}
        </AccordionContent>
      </AccordionItem>

      {/* Product Details */}
      <AccordionItem value="details">
        <AccordionTrigger className="text-lg font-semibold">
          Product details
        </AccordionTrigger>
        <AccordionContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                Version
              </div>
              <p className="font-medium">{asset.version || "1.0.0"}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Download className="h-4 w-4" />
                File Size
              </div>
              <p className="font-medium">{formatFileSize(asset.file_size)}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Published
              </div>
              <p className="font-medium">
                {asset.published_at
                  ? format(new Date(asset.published_at), "MMM d, yyyy")
                  : "Not published"}
              </p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Last Updated
              </div>
              <p className="font-medium">
                {format(new Date(asset.updated_at), "MMM d, yyyy")}
              </p>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>

      {/* License */}
      <AccordionItem value="license">
        <AccordionTrigger className="text-lg font-semibold">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            License Information
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="space-y-3">
            <Badge variant="outline" className="text-sm">
              {getLicenseInfo(asset.license_type).label}
            </Badge>
            <p className="text-sm text-muted-foreground">
              {getLicenseInfo(asset.license_type).description}
            </p>
            {asset.license_type === "uefn_map_use" && (
              <div className="rounded-md bg-success/10 border border-success/20 p-3 text-sm">
                <p className="font-medium text-success">💰 You keep 100% of island revenue</p>
                <p className="text-xs text-muted-foreground mt-1">This is a one-time purchase. No royalties or rev share to the seller.</p>
              </div>
            )}
            {asset.license_type === "revenue_share" && (
              <div className="rounded-md bg-warning/10 border border-warning/20 p-3 text-sm">
                <p className="font-medium text-warning">📊 Revenue share applies</p>
                <p className="text-xs text-muted-foreground mt-1">Check seller's terms for the exact percentage of island revenue shared.</p>
              </div>
            )}
            {asset.license_type === "custom" && asset.custom_license && (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap border-l-2 border-muted pl-3">
                {asset.custom_license}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              By purchasing, you agree to the <a href="/licenses" className="text-primary hover:underline">license terms</a> for this asset.
            </p>
          </div>
        </AccordionContent>
      </AccordionItem>

      {/* Version History */}
      <VersionHistory assetId={asset.id} currentVersion={asset.version} hasAccess={hasAccess} />
    </Accordion>
  );
}
